/**
 * The DOM binder for the death cinematic (Player duels, PRD #252 story 21, Duel
 * slice E, ADR-0136) - the presentation half of the pure {@link
 * './deathOverlay'} state machine.
 *
 * The pure module decides *what* the local player who died should see this frame
 * (a black-veil alpha, a "You are dead" alpha, whether the game view is grayscale);
 * this binder applies that {@link DeathOverlayView} to the DOM: the `#death-overlay`
 * black veil, its `#death-message` line, and a `death-grayscale` class toggled on
 * the game canvas so the rendered world desaturates with a single CSS filter (no
 * Pixi filter, so it needs no WebGL and never touches the headless render tests).
 * Like the splash/HUD/reconnect modules it owns only the wiring; the markup and
 * styling live in `index.html`, and it reads no Pixi and no socket. Every element
 * is looked up once and any that is missing is tolerated (a no-op), so partial
 * markup - or a headless test that mounts a subset - never throws.
 */
import type { DeathOverlayView } from './deathOverlay';

/** Optional hooks for {@link initDeathScreen}; all optional so headless tests pass none. */
export interface DeathScreenOptions {
  /** The document to wire (defaults to the live `document`); injected by tests. */
  doc?: Document;
  /**
   * The game view element the grayscale filter applies to - the Pixi canvas. The
   * `death-grayscale` CSS class greys the whole rendered world in one filter.
   * Absent (a headless test with no canvas) simply skips the desaturation toggle.
   */
  canvas?: HTMLElement | null;
}

/** The handle `main.ts` drives the death cinematic through, once per frame. */
export interface DeathScreenController {
  /** Apply this frame's {@link DeathOverlayView}: veil alpha, message alpha, grayscale. */
  render(view: DeathOverlayView): void;
}

/**
 * Wire the `#death-overlay` veil (declared **hidden** in `index.html` - it only
 * ever appears when the local player dies) and the canvas grayscale toggle into a
 * handle `main.ts` renders each frame from the pure state machine. The veil's
 * opacity and its "You are dead" line's opacity are set imperatively (the fade is
 * the state machine's, frame-accurate to the death beat - no CSS transition), and
 * the veil is fully removed from the layout + a11y tree once transparent so a
 * lingering invisible veil never eats a click.
 */
export function initDeathScreen(opts: DeathScreenOptions = {}): DeathScreenController {
  const doc = opts.doc ?? document;
  const root = doc.getElementById('death-overlay');
  const message = doc.getElementById('death-message');
  const canvas = opts.canvas ?? null;

  return {
    render(view) {
      if (root) {
        root.style.opacity = String(view.blackAlpha);
        // A transparent veil is fully gone: removed from layout (so the grayscale
        // ghost view underneath is interactive) and from the a11y tree.
        const visible = view.blackAlpha > 0;
        root.style.pointerEvents = visible ? 'auto' : 'none';
        root.hidden = !visible;
      }
      if (message) message.style.opacity = String(view.messageAlpha);
      // Desaturate the whole rendered world with one CSS filter on the canvas.
      canvas?.classList.toggle('death-grayscale', view.grayscale);
    },
  };
}
